import { defer, find } from 'lodash';

import { geocodeLocation } from 'shared/app/utils/geocode-location';
import { getPositionFromMapInstance } from 'shared/app/utils/map-utils';
import { parseResponseOrThrow } from 'shared/app/utils/fetch-utils';
import { showUnexpectedErrorNotification } from 'shared/app/shell';

import {
  expandedStoreActiveSelector,
  expandedStoreNumberSelector,
  fetchParametersSelector,
  fetchUrlSelector,
  inMenuStoreLocatorSelector,
  locationStateSelector,
  mapInstanceSelector,
} from '../selectors';

import {
  FETCH_LOCATIONS,
  FETCH_LOCATIONS_SUCCESS,
  FETCH_LOCATIONS_ERROR,
  SELECT_LOCATION,
  FETCH_ERROR,
  SET_PLACE_NAME,
  PLACE_NAME_NOT_FOUND,
  HIGHLIGHT_LOCATION,
  UNHIGHLIGHT_LOCATION,
  FETCH_AT_PLACENAME,
  FAVORITE_LOCATION_PENDING,
  FAVORITE_LOCATION_SUCCESS,
  FAVORITE_LOCATION_FAILURE,
} from './types';

export const setPlaceName = (placeName) => {
  return (dispatch) => {
    geocodeLocation({
      location: placeName,
      success: (coordinates) => {
        dispatch({
          type: FETCH_AT_PLACENAME,
          payload: { placeName, coordinates },
        });
      },
      error: (err) => {
        if (err.placeNameNotFound) {
          dispatch({ type: PLACE_NAME_NOT_FOUND });
        }
      },
      fallback: () => {
        dispatch({ type: SET_PLACE_NAME, payload: { placeName } });
      },
    });
  };
};

// The map should never move when the user selects a store by clicking on a
// marker, this works along with 'override' to keep the map in its place.
export const selectLocation = (
  locationNumber,
  {
    location,
    expanded = false,
    shouldScroll = false,
    preventMovement = false,
  } = {
    expanded: false,
    shouldScroll: false,
    preventMovement: false,
  }
) => {
  return (dispatch, getState) => {
    const mapInstance = mapInstanceSelector(getState());
    const mapPosition = getPositionFromMapInstance(mapInstance);

    dispatch({
      type: SELECT_LOCATION,
      payload: {
        locationNumber,
        mapPosition,
        location,
        expanded,
        shouldScroll,
        preventMovement,
      },
    });
  };
};

// APIs used:
//   delete: OAPI_DOCS/index.php?title=Delete_Favorite_Store
//   add/update: OAPI_DOCS/index.php?title=Add/Update_Favorite_Store
export const makeLocationFavorite = (location) => {
  return (dispatch, getState, { apiFetch }) => {
    const isFavorite = !location?.isFavorite;
    const storeFacilityNumber = location.store.storeNumber;
    const storeNumber =
      storeFacilityNumber?.indexOf('-') > 0
        ? storeFacilityNumber.substring(0, storeFacilityNumber.indexOf('-'))
        : storeFacilityNumber;

    dispatch({
      type: FAVORITE_LOCATION_PENDING,
      payload: {
        storeNumber: storeFacilityNumber,
        isFavorite,
      },
    });

    const url = isFavorite
      ? `/apiproxy/v1/stores/favorites`
      : `/apiproxy/v1/stores/favorites/${storeNumber}`;
    const options = {
      method: isFavorite ? 'POST' : 'DELETE',
      body: isFavorite ? { storeNumber } : null,
    };

    return apiFetch(url, options)
      .then(() =>
        dispatch({
          type: FAVORITE_LOCATION_SUCCESS,
          payload: {
            storeNumber: storeFacilityNumber,
            isFavorite,
          },
        })
      )
      .catch((error) =>
        dispatch({
          type: FAVORITE_LOCATION_FAILURE,
          error,
          payload: {
            storeNumber: storeFacilityNumber,
            isFavorite: !isFavorite,
          },
        })
      );
  };
};

export const highlightLocation = (locationNumber) => {
  return { type: HIGHLIGHT_LOCATION, locationNumber };
};

export const unhighlightLocation = () => {
  return { type: UNHIGHLIGHT_LOCATION };
};

const resolveSelectedStoreNumber = ({
  stores,
  inMenuStoreLocator,
  queryStoreNumber,
}) => {
  const firstStoreNumber = stores?.[0]?.store?.storeNumber;

  if (queryStoreNumber) {
    return queryStoreNumber;
  }

  if (inMenuStoreLocator) {
    const firstMopEligibleStore = find(stores.store, {
      open: true,
      mobileOrdering: { availability: 'READY' },
    });
    return firstMopEligibleStore
      ? firstMopEligibleStore.storeNumber
      : firstStoreNumber;
  }
  return firstStoreNumber;
};

export const fetchLocations = () => {
  return (dispatch, getState, { fetch }) => {
    const state = getState();
    const locationQuery = fetchParametersSelector(state);
    const url = fetchUrlSelector(state);
    const expandedStoreNumber = expandedStoreNumberSelector(state);
    const isExpanded = expandedStoreActiveSelector(state);

    if (!url) {
      return null;
    }

    dispatch({ type: FETCH_LOCATIONS, payload: { url, locationQuery } });

    const options = {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        // Add xhr header so apiproxy/v1/locations can attempt to limit requests to "local" ones.
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
    };

    return fetch(url, options)
      .then(parseResponseOrThrow)
      .then((stores) => {
        const newState = getState();
        // check whether this is the latest query
        // if not, it means there's a newer query
        // pending. By stopping here, we avoid
        // unnecessary thrashing of the UI.
        const currentQueryUrl =
          locationStateSelector(newState).locationQueryUrl;

        if (url !== currentQueryUrl) {
          return;
        }

        const inMenuStoreLocator = inMenuStoreLocatorSelector(newState);
        const selectedStoreNumber = resolveSelectedStoreNumber({
          stores,
          inMenuStoreLocator,
          queryStoreNumber: locationQuery.storeNumber,
        });

        dispatch({
          type: FETCH_LOCATIONS_SUCCESS,
          payload: {
            stores,
            selectedStoreNumber,
            expandedStoreNumber: isExpanded ? expandedStoreNumber : null,
          },
        });
      })
      .catch((err) => {
        const error = {
          status: err.status,
          statusText: err.message,
        };
        dispatch({ type: FETCH_LOCATIONS_ERROR });
        dispatch({ type: FETCH_ERROR, err: error });
        if (error.status >= 500) {
          defer(() => dispatch(showUnexpectedErrorNotification()));
        }
      });
  };
};
